import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { TextInput } from '@core/components/Inputs';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { Grid, Tooltip } from '@material-ui/core';
import { cancelarAlert } from 'app/services/alerts';
import imovelApi from 'app/services/api/imovelApi';
import * as Actions from 'app/store/actions';
import { normalizarCodigoCib, validateCodigoCib } from 'app/utils/validation';
import { FormikProvider, useFormik } from 'formik';
import { isEqual } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { MdHelp, MdHome } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import CampoComEspacoIconeAjuda, { ESPACO_ICONE_AJUDA } from 'app/components/CampoComEspacoIconeAjuda';
import EnderecoForm from '../../../../pessoa/Endereco/EnderecoForm';

const TAMANHO_BASE_CODIGO_CIB = 7;

const TAMANHO_CODIGO_CIB = 8;

const formatarCodigoCib = (codigoCib = '') =>
  codigoCib.length > TAMANHO_BASE_CODIGO_CIB
    ? `${codigoCib.slice(0, TAMANHO_BASE_CODIGO_CIB)}-${codigoCib.slice(TAMANHO_BASE_CODIGO_CIB)}`
    : codigoCib;

const AJUDA_CODIGO_CIB =
  'Informe o Código CIB do imóvel, composto por 8 caracteres. Ele identifica o imóvel em âmbito nacional e é diferente da inscrição imobiliária do IPTU.';

const MSG_CODIGO_CIB_TAMANHO = `O código CIB deve ter ${TAMANHO_CODIGO_CIB} posições.`;

const MSG_CODIGO_CIB_INVALIDO = 'Código CIB inválido. Confira o dígito verificador.';

const MAX_LENGTHS_ENDERECO = {
  logradouro: 255,
  numero: 60,
  complemento: 156,
  bairro: 60,
};

const ImovelSchema = yup.object().shape({
  nome: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o nome')),
  codigoCib: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o código CIB'))
    .length(TAMANHO_CODIGO_CIB, MSG_CODIGO_CIB_TAMANHO)
    .test('codigoCib', MSG_CODIGO_CIB_INVALIDO, function(codigoCib) {
      return !codigoCib || validateCodigoCib(codigoCib);
    }),
  endereco: yup.object().shape({
    cep: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o CEP')),
    logradouro: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o logradouro')),
    numero: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o número')),
    bairro: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o bairro')),
    uf: yup
      .object()
      .nullable()
      .test('uf', mensagemCampoObrigatorio('a UF'), function(uf) {
        return !!uf?.id;
      }),
    cidade: yup
      .object()
      .nullable()
      .test('cidade', mensagemCampoObrigatorio('a cidade'), function(cidade) {
        return !!cidade?.id;
      }),
  }),
});

const getInitialValues = (imovel) => ({
  id: imovel?.id,
  nome: imovel?.nome || '',
  inscricaoFiscal: imovel?.inscricaoFiscal || '',
  codigoCib: imovel?.codigoCib || '',
  endereco: {
    cep: imovel?.cep || '',
    logradouro: imovel?.logradouro || '',
    numero: imovel?.numero || '',
    complemento: imovel?.complemento || '',
    uf: imovel?.estadoId ? { id: imovel.estadoId, sigla: imovel.siglaUf, nome: imovel.siglaUf } : undefined,
    cidade: imovel?.cidadeId
      ? { id: imovel.cidadeId, nome: imovel.nomeCidade, codigo: imovel.codigoIbgeCidade }
      : undefined,
    bairro: imovel?.bairro || '',
    referencia: imovel?.referencia || '',
  },
});

const toRequest = (values) => ({
  nome: values.nome,
  inscricaoFiscal: values.inscricaoFiscal || null,
  codigoCib: values.codigoCib,
  cep: values.endereco?.cep,
  logradouro: values.endereco?.logradouro,
  numero: values.endereco?.numero,
  complemento: values.endereco?.complemento || null,
  bairro: values.endereco?.bairro,
  estadoId: values.endereco?.uf?.id,
  cidadeId: values.endereco?.cidade?.id,
  referencia: values.endereco?.referencia || null,
});

const normalizarParaComparacao = (payload = {}) => ({
  nome: payload.nome || '',
  inscricaoFiscal: payload.inscricaoFiscal || null,
  codigoCib: payload.codigoCib || '',
  cep: payload.cep?.replace(/\D/g, '') || '',
  logradouro: payload.logradouro || '',
  numero: payload.numero || '',
  complemento: payload.complemento || null,
  bairro: payload.bairro || '',
  estadoId: payload.estadoId != null ? Number(payload.estadoId) : null,
  cidadeId: payload.cidadeId != null ? Number(payload.cidadeId) : null,
  referencia: payload.referencia || null,
});

const ImovelModal = ({ open, imovel, onSalvar, onFechar }) => {
  const dispatch = useDispatch();
  const valoresOriginaisRef = useRef(null);

  const showError = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'error' }));
  };

  const onSubmitForm = async (values) => {
    try {
      const response = values.id
        ? await imovelApi.put(values.id, toRequest(values))
        : await imovelApi.post(toRequest(values));

      dispatch(
        Actions.showMessage({
          message: values.id ? 'Imóvel editado com sucesso' : 'Imóvel salvo com sucesso',
          variant: 'success',
        })
      );

      onSalvar(response.data);
    } catch (error) {
      showError(error?.response?.data?.message || 'Não foi possível salvar o imóvel');
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(imovel),
    validationSchema: ImovelSchema,
    onSubmit: onSubmitForm,
  });

  const { values, handleChange, handleBlur, errors, touched, submitCount } = formik;

  const { resetForm } = formik;
  useEffect(() => {
    if (open) {
      const initialValues = getInitialValues(imovel);
      resetForm({ values: initialValues });
      valoresOriginaisRef.current = normalizarParaComparacao(toRequest(initialValues));
    }
  }, [open, imovel, resetForm]);

  const possuiAlteracao = () =>
    !isEqual(normalizarParaComparacao(toRequest(formik.values)), valoresOriginaisRef.current);

  const _errors = {
    nome: (submitCount > 0 || touched?.nome) && errors?.nome,
    codigoCib: (submitCount > 0 || touched?.codigoCib) && errors?.codigoCib,
  };
  const handleChangeCodigoCib = (event) => {
    handleChange({
      target: {
        id: 'codigoCib',
        value: normalizarCodigoCib(event.target.value).slice(0, TAMANHO_CODIGO_CIB),
      },
    });
  };

  const handleCancelar = () => {
    if (possuiAlteracao()) {
      cancelarAlert({ confirmar: onFechar });
    } else {
      onFechar();
    }
  };

  return (
    <SimpleDialog fullScreen open={open} onClose={handleCancelar} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={() => {
            formik.handleSubmit();
          }}
          fullScreen
          header={{
            title: 'Cadastro do imóvel',
            icon: <MdHome />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCancelar}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit" styles={undefined} variant={'contained'}>
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={
            <div className="h-full max-h-full w-full">
              <Grid container spacing={3} className="mt-16 mb-16">
                <Grid item xs={12}>
                  <CampoComEspacoIconeAjuda>
                    <TextInput
                      autoFocus={!imovel?.id}
                      required
                      fullWidth
                      label="Nome"
                      id="nome"
                      value={values.nome}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      maxLength={100}
                      touched={!!_errors.nome}
                      error={_errors.nome}
                    />
                  </CampoComEspacoIconeAjuda>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextInput
                    label="Inscrição fiscal / imobiliária"
                    id="inscricaoFiscal"
                    value={values.inscricaoFiscal}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={30}
                  />
                </Grid>
                <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TextInput
                      required
                      fullWidth
                      label="Código CIB"
                      id="codigoCib"
                      value={formatarCodigoCib(values.codigoCib)}
                      onChange={handleChangeCodigoCib}
                      onBlur={handleBlur}
                      maxLength={TAMANHO_CODIGO_CIB + 1}
                      placeholder="AAAAAAA-D"
                      touched={!!_errors.codigoCib}
                      error={_errors.codigoCib}
                    />
                  </div>
                  <Tooltip title={AJUDA_CODIGO_CIB} placement="right-end">
                    <span style={{ ...ESPACO_ICONE_AJUDA, marginTop: 18, display: 'flex', color: 'rgba(0, 0, 0, 0.60)' }}>
                      <MdHelp size={18} />
                    </span>
                  </Tooltip>
                </Grid>

                <Grid item xs={12}>
                  <span style={{ fontSize: '15px', color: 'black', fontWeight: '500' }}>Endereço</span>
                </Grid>

                <EnderecoForm
                  basePath="endereco"
                  maxLengths={MAX_LENGTHS_ENDERECO}
                  reservarEspacoIconeAjuda
                  autoFocusCep={false}
                />
              </Grid>
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ImovelModal;
